import React, { useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logger from './utils/logger';
import './utils/firebase-startup-patch'; // Apply critical Firebase fixes on startup
import { isIOSPlatform, isCapacitorPlatform } from './utils/platform-detection';
import { initializeNativePlugins } from './services/native-plugins';
import { setupIOSLifecycleHandlers } from './services/ios-lifecycle.service';
import { searchService } from './services/search.service';
import { initializeOfflineSupport, cleanupOfflineSupport } from './services/offline/offline-init.service';
import { usePlatformClass } from './hooks/usePlatformClass';
import { initializePreloading } from './utils/route-preloader';
import { FirebaseProvider } from './providers/FirebaseProvider.simple';
import ThemeProvider from './providers/ThemeProvider';
import AchievementProvider from './providers/AchievementProvider';
import AppRoutes from './routes';
import OfflineIndicator from './components/OfflineIndicator';
import NotificationManager from './components/NotificationManager';
import ErrorBoundary from './components/ErrorBoundary';
import ToastContainer from './components/UI/ToastContainer';
import DeepLinkHandler from './components/DeepLinkHandler';

const App: React.FC = () => {
  const { t } = useTranslation();
  const platformLogger = logger.module('Platform');
  
  // Apply platform-specific class to body
  usePlatformClass();
  
  // Log environment information on app startup
  // Initialize search service
  useEffect(() => {
    // Initialize the PostgreSQL search service
    const initializeSearch = async () => {
      try {
        const initialized = await searchService.initialize();
        if (initialized) {
          logger.info('🔍 PostgreSQL search service initialized successfully');
        } else {
          logger.warn('⚠️ PostgreSQL search service initialization failed, will use fallback search');
        }
      } catch (error) {
        logger.error('❌ Error initializing PostgreSQL search service:', error);
      }
    };

    // Call the initialization function
    initializeSearch();
  }, []);

  useEffect(() => {
    if (isIOSPlatform()) {
      // Initialize native plugins for iOS
      platformLogger.info('📱 iOS platform: Initializing native plugins');
      initializeNativePlugins();
      
      // Simple iOS lifecycle setup - production ready
      setupIOSLifecycleHandlers();
    } else if (isCapacitorPlatform()) {
      platformLogger.info('📱 Capacitor native platform detected');
      initializeNativePlugins();
    } else {
      platformLogger.info('🌐 Web platform detected');
    }
    
    
    // Initialize offline support
    initializeOfflineSupport();
    
    // Initialize route preloading for better performance
    initializePreloading();
    
    // Cleanup on unmount
    return () => {
      cleanupOfflineSupport();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // platformLogger is stable, this effect should only run once on mount

  // Simple fallback UI for critical errors
  const appFallback = (
    <div style={{ padding: 20, textAlign: 'center' }}>
      <h2>{t('common.somethingWentWrong')}</h2>
      <p>{t('common.appError')}</p>
      <button 
        onClick={() => window.location.reload()}
        style={{ 
          padding: '8px 16px', 
          background: '#3b82f6', 
          color: 'white', 
          border: 'none', 
          borderRadius: '4px', 
          cursor: 'pointer',
          marginTop: 16
        }}
      >
        {t('common.refresh')}
      </button>
    </div>
  );

  return (
    <ErrorBoundary fallback={appFallback}>
      <ThemeProvider>
        <FirebaseProvider>
          <Router>
            <ErrorBoundary fallback={
              <Router>
                <div style={{ padding: 20 }}>
                  <h2>{t('common.notificationSystemUnavailable')}</h2>
                  <p>{t('common.notificationSystemMessage')}</p>
                  <AppRoutes />
                </div>
              </Router>
            }>
              <DeepLinkHandler />
              <NotificationManager>
                <AchievementProvider>
                  <AppRoutes />
                  <OfflineIndicator />
                  <ToastContainer />
                </AchievementProvider>
              </NotificationManager>
            </ErrorBoundary>
          </Router>
        </FirebaseProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;